import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Heart } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useProfile } from "@/context/ProfileContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";

export default function Dashboard() {
  const { L } = useI18n();
  const { profile, season, options, hasProfile } = useProfile();
  const { user } = useAuth();
  const [looks, setLooks] = useState([]);

  useEffect(() => { api.get("/looks").then(({ data }) => setLooks(data)).catch(() => {}); }, []);

  const traits = options ? [
    ["Genere", options.genders, profile.gender],
    ["Età", options.age_groups, profile.age_group],
    ["Sottotono", options.undertones, profile.undertone],
    ["Forma viso", options.face_shapes, profile.face_shape],
    ["Corporatura", options.body_types, profile.body_type],
    ["Tipo pelle", options.skin_types, profile.skin_type],
  ] : [];

  return (
    <div className="bg-surface min-h-screen">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <h1 className="font-serif text-4xl sm:text-5xl">Ciao, {user?.name?.split(" ")[0] || "bellezza"} ✨</h1>
        <p className="mt-2 text-charcoal/55">Il tuo profilo personale di bellezza e stile</p>

        <div className="grid lg:grid-cols-[320px_1fr] gap-6 mt-8 items-start">
          <div className="bg-white rounded-2xl border border-rose-100 p-6 text-center">
            <Avatar profile={profile} options={options} size={240} lip={season?.palette?.[4]} blush={season?.palette?.[0]} />
            {season ? (
              <>
                <p className="mt-3 text-xs uppercase tracking-widest text-charcoal/40">La tua stagione</p>
                <p className="font-serif text-2xl">{L(season, "name")}</p>
                <span className="inline-block mt-1 text-xs font-bold text-white bg-gold-dark rounded-full px-3 py-1">{season.match}% match</span>
              </>
            ) : (
              <Link to="/quiz" className="mt-4 inline-block text-rose-500 font-semibold">Fai il quiz →</Link>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-rose-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-2xl">Il tuo profilo</h2>
                <Link to="/quiz" className="text-sm font-semibold text-rose-500">Modifica</Link>
              </div>
              {hasProfile ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="profile-traits">
                  {traits.map(([label, list, val]) => {
                    const o = (list || []).find((x) => x.id === val);
                    return (
                      <div key={label}>
                        <p className="text-xs uppercase tracking-widest text-charcoal/40">{label}</p>
                        <p className="font-medium">{o ? L(o, "label") : "—"}</p>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-charcoal/55">Non hai ancora configurato il tuo profilo. <Link to="/quiz" className="text-rose-500 font-semibold">Inizia ora</Link>.</p>}
            </div>

            <div className="bg-white rounded-2xl border border-rose-100 p-6">
              <h2 className="font-serif text-2xl mb-4 flex items-center gap-2"><Heart size={20} className="text-rose-500" /> Look salvati</h2>
              {looks.length ? (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="saved-looks">
                  {looks.map((l) => (
                    <div key={l.id} className="border border-rose-100 rounded-xl p-4 flex items-center gap-3">
                      <span className="w-10 h-10 rounded-full rose-gold-bg" />
                      <div><p className="font-medium">{l.season_name}</p><p className="text-xs text-charcoal/45">{new Date(l.created_at).toLocaleDateString()}</p></div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-charcoal/55">Nessun look salvato. Vai ai <Link to="/results" className="text-rose-500 font-semibold">risultati</Link> e salva il tuo preferito.</p>}
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <Link to="/results" className="bg-white rounded-2xl border border-rose-100 p-5 hover-lift"><Sparkles className="text-rose-500 mb-2" /><p className="font-serif text-lg">Analisi</p></Link>
              <Link to="/catalog/makeup" className="bg-white rounded-2xl border border-rose-100 p-5 hover-lift"><p className="font-serif text-lg">Make-up</p></Link>
              <Link to="/outfits" className="bg-white rounded-2xl border border-rose-100 p-5 hover-lift"><p className="font-serif text-lg">Outfit</p></Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
